/**
 * A page's filters as a collapsible LEFT column — the shape the Search & Track and Camera List
 * pages already use, made generic so every list page reads the same way:
 *
 *   <PageWithFilters filters={<FilterColumn …>sections</FilterColumn>}>
 *     results
 *   </PageWithFilters>
 *
 * The column folds to a 44px rail (with the active-filter count) and remembers that per page in
 * localStorage under `trinetra.filters.<storageKey>`. Its controls stay MOUNTED while folded, so
 * a half-typed search survives a fold. It is sticky and full height, scrolling inside itself.
 *
 * Put filters here; keep page ACTIONS (Add, Export, Refresh) and view toggles in the results'
 * own toolbar or in <PageTitle>. Removable active-filter chips belong above the results, never
 * inside the column — a folded column must not hide why the list is short.
 */
import { createContext, useCallback, useContext, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { AdjustmentsHorizontalIcon, CheckIcon, ChevronDoubleLeftIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { PaneRail } from '../search/PaneControls';
import { FilterDropdown, OptionList } from '../search/FilterControls';
import { PAGE_FILL_H } from './TitleBar';

const storageKeyFor = (key: string) => `trinetra.filters.${key}`;

/**
 * FLUSH, as on Search & Track: a filter column that is the page's own (its PageWithFilters is
 * the page body) sits against the side nav and the title bar — no gutter, no card — full height,
 * with only a border on its right; the results take the gutter instead. The shell drops its
 * padding for such a page (`main:has(> [data-filter-page=flush])`, see both Layouts).
 */
const FlushContext = createContext(false);
/** True inside a flush PageWithFilters — for a page's own column component (the camera list's). */
export const useFilterFlush = () => useContext(FlushContext);
/** A flush column's height: the screen under the title bar (h-14), with no page padding. */
export const FLUSH_FILL_H = 'h-[calc(100vh-3.5rem)]';
/** The outer frame of a filter column: flush (page body) or a card (inside a tab, a panel). */
export const filterColumnFrame = (flush: boolean, heightClass: string) => (flush
  ? `sticky top-0 shrink-0 self-start overflow-hidden border-r border-border bg-card ${FLUSH_FILL_H}`
  : `sticky top-0 shrink-0 self-start overflow-hidden rounded-lg border border-border bg-card shadow-card ${heightClass}`);

/** Open/closed state for a FilterColumn, remembered per page. */
export function useFilterColumn(storageKey: string, defaultOpen = true) {
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(storageKeyFor(storageKey));
      return v === null ? defaultOpen : v === '1';
    } catch { return defaultOpen; }
  });
  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem(storageKeyFor(storageKey), next ? '1' : '0'); } catch { /* private window */ }
      return next;
    });
  }, [storageKey]);
  return { open, toggle };
}

export interface FilterColumnProps {
  open: boolean;
  onToggle: () => void;
  /** Column heading, e.g. "Alert filters". */
  label?: string;
  /** Filters currently narrowing the list — shown on the folded rail and beside "Clear". */
  activeCount?: number;
  onClearAll?: () => void;
  children: ReactNode;
  /** Height classes; defaults to filling the screen under the title bar. */
  heightClass?: string;
  /** Extra line under the heading row (e.g. a scope note). */
  header?: ReactNode;
  /** Extra classes on the root, e.g. `hidden lg:block` where a page keeps a bar on phones. */
  className?: string;
  /** `wide` = the Search & Track panel's width (the Alerts pages); default the compact one. */
  width?: 'normal' | 'wide';
}

export function FilterColumn({
  open, onToggle, label = 'Filters', activeCount = 0, onClearAll, children,
  heightClass = PAGE_FILL_H, header = null, className = '', width = 'normal',
}: FilterColumnProps) {
  // The Search & Track panel's look: a FILTERS heading with the active count, "Clear all", and a
  // square « that folds it to a rail. `wide` is that panel's width too.
  const w = width === 'wide' ? 'w-[19rem] xl:w-[20.5rem]' : 'w-[15rem] xl:w-[16.5rem]';
  const flush = useContext(FlushContext);
  return (
    <div
      className={`${filterColumnFrame(flush, heightClass)} transition-[width] duration-300 ease-in-out ${className} ${
        open ? w : 'w-11'
      }`}
    >
      <aside
        aria-label={label}
        aria-hidden={!open}
        className={`flex h-full flex-col transition-opacity duration-200 ${w} ${open ? 'opacity-100' : 'invisible opacity-0'}`}
      >
        <div className="flex shrink-0 items-center gap-2 border-b border-border px-4 py-2.5">
          <span className="flex min-w-0 items-center gap-2 truncate text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
            <AdjustmentsHorizontalIcon className="h-4 w-4 shrink-0" />
            {label}
          </span>
          {activeCount > 0 && (
            <span className="min-w-[1.25rem] shrink-0 rounded-full bg-brand-light px-1.5 text-center text-[11px] font-semibold leading-5 text-brand">{activeCount}</span>
          )}
          {onClearAll && activeCount > 0 && (
            <button type="button" onClick={onClearAll} className="ml-auto shrink-0 text-[12px] text-muted-foreground transition-colors hover:text-foreground">
              Clear all
            </button>
          )}
          <button
            type="button"
            onClick={onToggle}
            title={`Hide ${label.toLowerCase()}`}
            aria-label={`Hide ${label.toLowerCase()}`}
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:border-brand hover:text-brand ${onClearAll && activeCount > 0 ? '' : 'ml-auto'}`}
          >
            <ChevronDoubleLeftIcon className="h-4 w-4" />
          </button>
        </div>
        {header}
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4" style={{ scrollbarWidth: 'thin' }}>
          {children}
        </div>
      </aside>
      {!open && (
        <PaneRail
          label={label}
          Icon={AdjustmentsHorizontalIcon}
          count={activeCount}
          onExpand={onToggle}
          opens="right"
          className="absolute inset-0"
        />
      )}
    </div>
  );
}

/** One titled block in a FilterColumn, with an optional action (usually "Clear") on the right. */
export function FilterSection({ title, hint = null, action = null, children }: {
  title: string; hint?: ReactNode; action?: ReactNode; children: ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
          {hint && <span className="ml-1.5 font-normal normal-case tracking-normal">{hint}</span>}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

/** A full-width checkable row (checkbox, or radio dot with `radio`), optional swatch and count. */
export function FilterCheck({ checked, onClick, label, count = null, swatch = null, radio = false }: {
  checked: boolean; onClick: () => void; label: ReactNode; count?: number | null; swatch?: string | null; radio?: boolean;
}) {
  return (
    <button
      type="button"
      role={radio ? 'radio' : 'checkbox'}
      aria-checked={checked}
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] text-foreground transition-colors ${
        checked ? 'bg-brand-light' : 'hover:bg-muted'
      } ${count === 0 && !checked ? 'opacity-50' : ''}`}
    >
      {radio ? (
        <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${checked ? 'border-brand bg-card' : 'border-border bg-card'}`}>
          {checked && <span className="h-2 w-2 rounded-full bg-brand" />}
        </span>
      ) : (
        <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${checked ? 'border-brand bg-brand text-white' : 'border-border bg-card'}`}>
          {checked && <CheckIcon className="h-3 w-3 stroke-[3]" />}
        </span>
      )}
      {swatch && <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: swatch }} />}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== null && <span className="shrink-0 text-[12px] tabular-nums text-muted-foreground">{count.toLocaleString()}</span>}
    </button>
  );
}

/** The search box used at the top of most filter columns. */
export function FilterSearch({ value, onChange, placeholder = 'Search…' }: {
  value: string; onChange: (value: string) => void; placeholder?: string;
}) {
  return (
    <div className="relative">
      <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-border bg-card py-2 pl-8 pr-7 text-[13px] text-foreground placeholder-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring"
      />
      {value && (
        <button type="button" onClick={() => onChange('')} aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded text-muted-foreground hover:text-foreground">
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

/**
 * One-click choices in a grid — the Search & Track "When" presets. For a handful of short
 * options (severity, a time window, rows per page) where a dropdown would hide them.
 */
export function FilterChips<T extends string | number>({ value, options, onChange, cols = 3 }: {
  value: T; options: { value: T; label: string; title?: string }[]; onChange: (v: T) => void; cols?: 2 | 3 | 4;
}) {
  const grid = cols === 2 ? 'grid-cols-2' : cols === 4 ? 'grid-cols-4' : 'grid-cols-3';
  return (
    <div className={`grid ${grid} gap-1.5`} role="radiogroup">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button key={String(o.value)} type="button" role="radio" aria-checked={on} title={o.title} onClick={() => onChange(o.value)}
            className={`truncate rounded-lg border px-1.5 py-1.5 text-[12px] transition-colors ${
              on ? 'border-brand bg-brand-light font-semibold text-foreground' : 'border-border bg-card text-foreground hover:bg-muted'
            }`}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Pick ONE value of a dimension. A short list is shown as rows (every choice visible at once);
 * past `inlineMax` it becomes the Search & Track row — "Camera · All ▾" — whose list opens with a
 * search box, so thirty cameras or forty alert names are found by typing, not scrolling.
 * `value` '' = all.
 */
export function FilterPick({
  label, value, options, onChange, allLabel = 'All', inlineMax = 6, searchPlaceholder, allCount = null,
}: {
  label: string;
  value: string;
  options: { value: string; label: string; count?: number }[];
  onChange: (v: string) => void;
  allLabel?: string;
  inlineMax?: number;
  searchPlaceholder?: string;
  /** The count beside "All" (rows only). */
  allCount?: number | null;
}) {
  // A value no longer offered (aged out, deleted) stays listed — at 0 — so it can be cleared.
  const list = value && !options.some((o) => o.value === value) ? [...options, { value, label: value, count: 0 }] : options;
  if (list.length <= inlineMax) {
    return (
      <div className="space-y-0.5">
        <FilterCheck radio checked={!value} onClick={() => onChange('')} label={allLabel} count={allCount} />
        {list.map((o) => (
          <FilterCheck key={o.value} radio checked={value === o.value} onClick={() => onChange(o.value)} label={o.label} count={o.count ?? null} />
        ))}
      </div>
    );
  }
  const chosen = list.find((o) => o.value === value);
  return (
    <FilterDropdown variant="row" label={label} summary={chosen ? chosen.label : allLabel} active={!!value} panelClassName="w-72">
      {(close) => (
        <OptionList
          mode="single"
          searchable
          searchPlaceholder={searchPlaceholder || `Search ${label.toLowerCase()}…`}
          options={[{ value: '', label: allLabel, count: allCount ?? undefined }, ...list.map((o) => ({ value: o.value, label: o.label, count: o.count }))]}
          selected={[value]}
          onChange={(next) => onChange(next[0] || '')}
          close={close}
        />
      )}
    </FilterDropdown>
  );
}

/** Class for a native <select>/<input> inside a filter column, full width. */
export const FILTER_CONTROL =
  'w-full rounded-lg border border-border bg-input px-2.5 py-1.5 text-[13px] text-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring';

/**
 * Filter column + results side by side. `filters` is usually a <FilterColumn>; the results side
 * gets the remaining width and must not set its own horizontal margin.
 */
export function PageWithFilters({ filters = null, children, className = '', toolbar = null }: {
  /** The filter column — none for a page that only wants the flush toolbar (PageWithToolbar). */
  filters?: ReactNode; children: ReactNode; className?: string;
  /**
   * The results' top bar (status tabs, a view switch). On a flush page it is a white strip across
   * the results, touching the title bar and the filter column, sticky as the list scrolls — the
   * list under it sits straight on the page background, as on Search & Track. Elsewhere a card.
   */
  toolbar?: ReactNode;
}) {
  // Flush when it IS the page body (a direct child of the shell's <main>); inside a tab or a
  // panel it stays a card in the gutter. Measured before paint, so there is no jump.
  const ref = useRef<HTMLDivElement>(null);
  const [flush, setFlush] = useState(false);
  useLayoutEffect(() => { setFlush(ref.current?.parentElement?.tagName === 'MAIN'); }, []);
  return (
    <div ref={ref} data-filter-page={flush ? 'flush' : undefined} className={`flex items-start ${flush ? 'min-h-full' : 'gap-3'} ${className}`}>
      {filters && <FlushContext.Provider value={flush}>{filters}</FlushContext.Provider>}
      {flush && toolbar ? (
        <div className="min-w-0 flex-1">
          <div className="sticky top-0 z-10 border-b border-border bg-card">{toolbar}</div>
          <div className="space-y-3 p-2 sm:p-3">{children}</div>
        </div>
      ) : (
        <div className={`min-w-0 flex-1 space-y-3 ${flush ? 'p-2 sm:p-3' : ''}`}>
          {toolbar && <div className="overflow-hidden rounded-xl border border-border bg-card shadow-card">{toolbar}</div>}
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * A page with no filter column but the same top strip: its toolbar (tabs, a view switch) white
 * and flush against the title bar and the side nav, the page itself on the background.
 */
export function PageWithToolbar({ toolbar, children, className = '' }: { toolbar: ReactNode; children: ReactNode; className?: string }) {
  return <PageWithFilters toolbar={toolbar} className={className}>{children}</PageWithFilters>;
}
